<template>
  <div v-if="internalTenant" class="tenant-form">
    <el-form class="tenant-edit-form"
             ref="general-form"
             @submit.native.prevent="onFormSubmit"
             :rules="formRules"
             :model="internalTenant"
             label-width="170px">

      <!-- Basic Information -->
      <div class="form-card">
        <div class="form-card__header">
          <div class="form-card__icon">
            <i class="el-icon-s-cooperation"></i>
          </div>
          <h3 class="form-card__title">{{ $t('settings.tenantSection') }}</h3>
        </div>
        <div class="form-card__body">
          <el-form-item :label="$t('settings.name')" :error="error" prop="name">
            <el-input v-model="internalTenant.name"></el-input>
          </el-form-item>

          <el-form-item :label="$t('settings.description')" prop="description">
            <el-input type="textarea" :rows="3" v-model="internalTenant.description"></el-input>
          </el-form-item>

          <el-form-item :label="$t('settings.logo')" prop="logo">
            <div class="logo-upload-container">
              <el-input v-model="internalTenant.logo" placeholder="Logo URL" class="logo-url-input"></el-input>
              <el-upload
                class="logo-uploader"
                action="#"
                :http-request="uploadLogo"
                :show-file-list="false"
                :before-upload="beforeLogoUpload">
                <img v-if="logoPreview" :src="logoPreview" class="logo-preview">
                <i v-else class="el-icon-plus logo-uploader-icon"></i>
              </el-upload>
            </div>
            <div class="logo-help-text" v-if="logoPreview">
              <el-button size="mini" type="danger" plain @click="removeLogo">{{ $t('base.delete') }}</el-button>
            </div>
          </el-form-item>
        </div>
      </div>

      <!-- Address Information -->
      <div class="form-card">
        <div class="form-card__header">
          <div class="form-card__icon">
            <i class="el-icon-location-information"></i>
          </div>
          <h3 class="form-card__title">{{ $t('settings.addressSection') }}</h3>
        </div>
        <div class="form-card__body">
          <el-form-item :label="$t('settings.address.street')" prop="address.street">
            <el-input v-model="internalTenant.address.street"></el-input>
          </el-form-item>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item :label="$t('settings.address.zipCode')" prop="address.zipCode">
                <el-input v-model="internalTenant.address.zipCode"></el-input>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item :label="$t('settings.address.city')" prop="address.city">
                <el-input v-model="internalTenant.address.city"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item :label="$t('settings.address.country')" prop="address.country">
                <el-input v-model="internalTenant.address.country"></el-input>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item :label="$t('settings.address.state')" prop="address.state">
                <el-input v-model="internalTenant.address.state"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </div>
      </div>

      <!-- Contact Information -->
      <div class="form-card">
        <div class="form-card__header">
          <div class="form-card__icon">
            <i class="el-icon-phone-outline"></i>
          </div>
          <h3 class="form-card__title">{{ $t('settings.contactSection') }}</h3>
        </div>
        <div class="form-card__body">
          <el-form-item :label="$t('settings.contact.name')" prop="contactInformation.name">
            <el-input v-model="internalTenant.contactInformation.name"></el-input>
          </el-form-item>

          <el-form-item :label="$t('settings.contact.email')" prop="contactInformation.email">
            <el-input v-model="internalTenant.contactInformation.email"></el-input>
          </el-form-item>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item :label="$t('settings.contact.phone')" prop="contactInformation.phone">
                <el-input v-model="internalTenant.contactInformation.phone"></el-input>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item :label="$t('settings.contact.fax')" prop="contactInformation.fax">
                <el-input v-model="internalTenant.contactInformation.fax"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item :label="$t('settings.contact.website')" prop="contactInformation.website">
            <el-input v-model="internalTenant.contactInformation.website"></el-input>
          </el-form-item>
        </div>
      </div>

      <div class="form-actions">
        <base-button
          type="primary"
          native-type="submit"
          :loading="loading">
          {{ mode === 'create' ? $t('settings.create') : $t('settings.save') }}
        </base-button>
      </div>
    </el-form>
  </div>
</template>
<script>
import BaseButton from '@/components/common/BaseButton';
import {fileStorageService} from '@/services/FileStorageService.class';

export default {
  name: "TenantForm",
  components: {BaseButton},
  props: {
    loading: {
      default: false
    },
    tenant: {
      default: null,
    },
    mode: {
      default: 'edit',
    },
    error: {
      default: ''
    }
  },
  data() {
    return {
      internalTenant: null,
      formRules: null,
      logoPreview: null,
      uploadLoading: false,
      currentFilename: null
    }
  },
  mounted() {
    if (this.mode === 'create') {
      this.internalTenant = this.getEmptyTenant();
      this.formRules = this.generateRules();
    }
  },
  watch: {
    tenant: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Clone the tenant to avoid modifying the prop directly
          this.internalTenant = {...newVal};

          // Initialize nested objects if they don't exist
          if (!this.internalTenant.address) {
            this.internalTenant.address = {street: '', zipCode: '', city: '', country: '', state: ''};
          }

          if (!this.internalTenant.contactInformation) {
            this.internalTenant.contactInformation = {name: '', email: '', phone: '', fax: '', website: ''};
          }

          this.formRules = this.generateRules();

          // Set logo preview if logo URL exists
          if (this.internalTenant.logo) {
            this.logoPreview = this.internalTenant.logo;

            // Extract filename from URL if possible
            try {
              const url = new URL(this.internalTenant.logo);
              const pathname = url.pathname;
              const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
              if (filename) {
                this.currentFilename = filename;
              }
            } catch (e) {
              console.warn('Could not extract filename from logo URL:', e);
            }
          }
        }
      }
    }
  },
  methods: {
    getEmptyTenant() {
      return {
        name: '',
        description: '',
        logo: '',
        address: {
          street: '',
          zipCode: '',
          city: '',
          country: '',
          state: ''
        },
        contactInformation: {
          name: '',
          email: '',
          phone: '',
          fax: '',
          website: ''
        }
      };
    },
    generateRules() {
      return {
        name: [
          {required: true, message: this.$t('settings.nameRequired'), trigger: 'blur'}
        ],
        'contactInformation.email': [
          {type: 'email', message: this.$t('settings.invalidEmail'), trigger: 'blur'}
        ]
      };
    },
    async onFormSubmit() {
      try {
        await this.$refs['general-form'].validate();
        this.$emit('submit', this.internalTenant);
      } catch (e) {
        // Validation failed
        console.error('Form validation failed:', e);
      }
    },
    beforeLogoUpload(file) {
      // Check file type
      const isImage = file.type.startsWith('image/');
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isImage) {
        this.$message.error(this.$t('settings.logo_upload_type_error'));
        return false;
      }
      if (!isLt2M) {
        this.$message.error(this.$t('settings.logo_upload_size_error'));
        return false;
      }

      return true;
    },
    uploadLogo(options) {
      this.uploadLoading = true;

      fileStorageService.uploadFile(options.file)
        .then(response => {
          // The response should contain the URL of the uploaded file
          this.internalTenant.logo = response.data;
          this.logoPreview = response.data;

          // Extract filename from the URL if possible
          try {
            const url = new URL(response.data);
            const pathname = url.pathname;
            const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
            if (filename) {
              // If we had a previous file, delete it
              if (this.currentFilename && this.currentFilename !== filename) {
                this.deleteOldLogo();
              }
              this.currentFilename = filename;
            }
          } catch (e) {
            console.warn('Could not extract filename from logo URL:', e);
          }

          this.$message.success(this.$t('settings.logo_upload_success'));
        })
        .catch(error => {
          console.error('Error uploading logo:', error);
          this.$message.error(this.$t('settings.logo_upload_error'));
        })
        .finally(() => {
          this.uploadLoading = false;
        });
    },
    removeLogo() {
      // If we have a current filename, delete the file
      if (this.currentFilename) {
        this.deleteOldLogo();
      }

      // Clear the logo URL and preview
      this.internalTenant.logo = '';
      this.logoPreview = null;
      this.currentFilename = null;
    },
    deleteOldLogo() {
      if (this.currentFilename) {
        fileStorageService.deleteFile(this.currentFilename)
          .catch(error => {
            console.error('Error deleting old logo:', error);
          });
      }
    }
  }
}
</script>

<style scoped lang="scss">
.tenant-form {
  padding: 0 15px;
}

.el-form {
  width: 100%;
}

.form-card {
  background: var(--bg-surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  margin-bottom: 20px;

  &__header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: var(--bg-surface);
      font-size: 16px;
    }
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__body {
    padding: 24px 20px 4px;
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.logo-upload-container {
  display: flex;
  align-items: flex-start;
}

.logo-url-input {
  flex: 1;
  margin-right: 10px;
}

.logo-uploader {
  border: 1px dashed var(--border);
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 100px;
  height: 100px;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: var(--primary);
  }
}

.logo-uploader-icon {
  font-size: 28px;
  color: var(--text-muted);
  width: 100px;
  height: 100px;
  line-height: 100px;
  text-align: center;
}

.logo-preview {
  width: 100px;
  height: 100px;
  display: block;
  object-fit: contain;
}

.logo-help-text {
  margin-top: 5px;
  text-align: right;
}

@media (max-width: 768px) {
  .form-card__body {
    padding: 20px 14px 4px;
  }

  .logo-upload-container {
    flex-direction: column;

    .logo-url-input {
      margin-right: 0;
      margin-bottom: 10px;
    }
  }
}
</style>
